"use client";

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Target, Pencil, Check, X } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Progress } from "@/app/_module/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useAgentWorkload } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";

type Goal = { type: string; value: number; duration: "daily" | "weekly" | "monthly" };
type Effective = Goal & { source: "agent" | "agency"; sourceAgencyId: string | null; sourceAgencyName: string | null };

const METRIC: Record<string, { label: string; prefix?: string; unit?: string }> = {
  premium: { label: "Premium sold", prefix: "$" },
  policies_submitted: { label: "Policies" },
  dials: { label: "Dials" },
  talk_time: { label: "Talk time", unit: "m" },
  completed_calls: { label: "Completed calls" },
  contacts: { label: "Contacts" },
  appointments: { label: "Appointments" },
  lead_count: { label: "Leads assigned" },
};
const WHEN: Record<string, string> = { daily: "today", weekly: "this week", monthly: "this month" };
const fmt = (n: number, t: string) => `${METRIC[t]?.prefix || ""}${Math.round(n).toLocaleString("en-US")}${METRIC[t]?.unit || ""}`;

/**
 * Agency-wide default goal + rollup. Hierarchy: agency default → sub-agency
 * override (a sub-agency owner sets its own default here) → agent override
 * (Lead Assignment → Set Goals). Agents with no goal of their own inherit.
 */
export default function AgencyDefaultGoalCard() {
  const { user } = useUserContext();
  const agencyId = user?.currentAgency?.agency?.id;
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ own: Goal | null; effective: Effective | null }>({
    queryKey: ["agencyDefaultGoal", agencyId],
    enabled: !!agencyId,
    queryFn: async () => {
      const res = await api.get<{ data: { own: Goal | null; effective: Effective | null } }>({ url: getApiUrl(`/agencies/${agencyId}/default-goal`), auth: true });
      return (res as any)?.data?.data ?? (res as any)?.data ?? { own: null, effective: null };
    },
    staleTime: 60_000,
  });
  const { data: workload } = useAgentWorkload();
  const agents: any[] = (workload as any)?.agents || [];

  const [editing, setEditing] = useState(false);
  const [type, setType] = useState("premium");
  const [duration, setDuration] = useState<"daily" | "weekly" | "monthly">("monthly");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const effective = data?.effective ?? null;
  const own = data?.own ?? null;
  const inheritedFromParent = !!effective && !own;

  // Rollup: every agent on the agency's metric + period (own goal or inherited).
  const rollup = useMemo(() => {
    if (!effective) return null;
    const rows = agents.filter((a) => a.goalType === effective.type && (a.goalDuration || "daily") === effective.duration && a.goalValue > 0);
    const current = rows.reduce((s, a) => s + Number(a.goalCurrent || 0), 0);
    const target = rows.reduce((s, a) => s + Number(a.goalValue || 0), 0);
    const onTarget = rows.filter((a) => Number(a.goalCurrent || 0) >= Number(a.goalValue || 0)).length;
    const inherited = rows.filter((a) => a.goalSource === "agency").length;
    return { current, target, onTarget, agents: rows.length, inherited, pct: target > 0 ? (current / target) * 100 : 0 };
  }, [agents, effective]);

  const save = async (goal: Goal | null) => {
    if (!agencyId) return;
    setSaving(true);
    try {
      await api.put({ url: getApiUrl(`/agencies/${agencyId}/default-goal`), auth: true, body: goal ? { goalType: goal.type, goalValue: goal.value, goalDuration: goal.duration } : { goalValue: null } });
      toast({ title: goal ? "Agency goal saved" : "Agency goal cleared", description: goal ? `${METRIC[goal.type]?.label || goal.type} ${fmt(goal.value, goal.type)} ${WHEN[goal.duration]} for every agent without their own goal.` : "Agents without their own goal no longer have one." });
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["agencyDefaultGoal", agencyId] });
      qc.invalidateQueries({ queryKey: ["agentWorkload", agencyId] });
    } catch (e) {
      toast({ title: "Not saved", description: transformErrorResponse(e).message || "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setType(effective?.type || "premium");
    setDuration(effective?.duration || "monthly");
    setValue(effective ? String(effective.value) : "");
    setEditing(true);
  };

  if (!agencyId || isLoading) return null;

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Target className="h-4 w-4" /></div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Agency goal</h3>
            {effective ? (
              <p className="text-sm text-gray-700">
                {METRIC[effective.type]?.label || effective.type}{" "}
                <span className="font-semibold tabular-nums">{fmt(effective.value, effective.type)}</span> {WHEN[effective.duration]}, per agent
                {inheritedFromParent && effective.sourceAgencyName && <span className="text-gray-500"> · inherited from {effective.sourceAgencyName}</span>}
              </p>
            ) : (
              <p className="text-sm text-gray-500">No agency-wide goal yet. Set one and every agent without a goal of their own gets it.</p>
            )}
            <p className="mt-0.5 text-xs text-gray-400">Agency default → sub-agency override (set here in that agency) → agent override (Lead Assignment → Set Goals).</p>
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={startEdit}><Pencil className="mr-1 h-3.5 w-3.5" />{own ? "Edit" : effective ? "Override here" : "Set goal"}</Button>
            {own && <Button size="sm" variant="ghost" disabled={saving} onClick={() => save(null)}>Clear</Button>}
          </div>
        )}
      </div>

      {editing && (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); const v = Number(value); if (!Number.isFinite(v) || v <= 0) return; save({ type, value: v, duration }); }}
        >
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(METRIC).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min={1} value={value} onChange={(e) => setValue(e.target.value)} placeholder={METRIC[type]?.prefix ? "Target in dollars" : "Target"} className="w-36" />
          <Select value={duration} onValueChange={(v) => setDuration(v as any)}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
          </Select>
          <Button type="submit" size="sm" disabled={saving || !value}><Check className="mr-1 h-3.5 w-3.5" />Save</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /></Button>
        </form>
      )}

      {rollup && rollup.agents > 0 && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-medium text-gray-900">Agency progress {WHEN[effective!.duration]}</span>
            <span className="tabular-nums text-gray-700">{fmt(rollup.current, effective!.type)} / {fmt(rollup.target, effective!.type)} · {rollup.pct.toFixed(0)}%</span>
          </div>
          <Progress value={Math.min(rollup.pct, 100)} className="mt-1.5 h-2" />
          <p className="mt-1 text-xs text-gray-500">
            {rollup.onTarget} of {rollup.agents} agents on target · {rollup.inherited} on the agency default, {rollup.agents - rollup.inherited} with their own {METRIC[effective!.type]?.label.toLowerCase()} goal. Per-agent progress is in the board below.
          </p>
        </div>
      )}
    </div>
  );
}
